import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './ServiceTaxModal.css';
import AppIcon from './icons/AppIcon';

export default function ServiceTaxModal({ projectId, onClose, onSave }) {
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadServiceTax();
  }, [projectId]);

  const loadServiceTax = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      const { data, error: err } = await supabase
        .from('bar_restaurant_service_tax')
        .select('percentage, enabled')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .maybeSingle();

      if (err) {
        console.error('Erro ao buscar taxa de serviço:', err);
        setError('Erro ao carregar configuração');
        return;
      }

      if (data) {
        setTaxPercentage(data.percentage || 0);
        setIsEnabled(data.enabled || false);
      }
    } catch (err) {
      console.error('Erro:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));

      // Verificar se já existe registro
      const { data: existing } = await supabase
        .from('bar_restaurant_service_tax')
        .select('id')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .maybeSingle();

      if (existing) {
        // Atualizar
        const { error: err } = await supabase
          .from('bar_restaurant_service_tax')
          .update({
            percentage: taxPercentage,
            enabled: isEnabled,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (err) throw err;
      } else {
        // Inserir
        const { error: err } = await supabase
          .from('bar_restaurant_service_tax')
          .insert({
            project_id: projectId,
            company_id: user.company_id,
            percentage: taxPercentage,
            enabled: isEnabled,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (err) throw err;
      }

      if (onSave) {
        onSave({ percentage: taxPercentage, enabled: isEnabled });
      }

      onClose();
    } catch (err) {
      console.error('Erro ao salvar taxa de serviço:', err);
      setError('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="service-tax-modal-overlay" onClick={onClose}>
        <div className="service-tax-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <AppIcon name="progress_activity" className="spinning" />
            <p>Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-tax-modal-overlay" onClick={onClose}>
      <div className="service-tax-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <AppIcon name="percent" />
            Configurar Taxa de Serviço
          </h2>
          <button className="close-btn" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              <AppIcon name="error" />
              {error}
            </div>
          )}

          {/* Toggle de Habilitação */}
          <div className="tax-section">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                disabled={saving}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">
                {isEnabled ? '✓ Ativado' : '○ Desativado'}
              </span>
            </label>
            <p className="section-description">
              Quando ativado, a taxa de serviço será incluída no prompt do garçom
            </p>
          </div>

          {/* Input de Percentual */}
          <div className="tax-section">
            <label htmlFor="taxInput" className="input-label">
              <AppIcon name="percent" />
              Percentual de Taxa
            </label>
            <div className="input-group">
              <input
                id="taxInput"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={taxPercentage}
                onChange={(e) => setTaxPercentage(parseFloat(e.target.value) || 0)}
                disabled={!isEnabled || saving}
                className="tax-input"
                placeholder="0"
              />
              <span className="input-suffix">%</span>
            </div>
            <p className="section-description">
              Digite o percentual de taxa de serviço (ex: 10 para 10%)
            </p>
          </div>

          {/* Preview */}
          {isEnabled && taxPercentage > 0 && (
            <div className="tax-preview">
              <h4>
                <AppIcon name="preview" />
                Exemplo de Cálculo
              </h4>
              <div className="preview-item">
                <span>Subtotal</span>
                <span>R$ 100,00</span>
              </div>
              <div className="preview-item">
                <span>Taxa de Serviço ({taxPercentage}%)</span>
                <span>R$ {(100 * taxPercentage / 100).toFixed(2)}</span>
              </div>
              <div className="preview-item total">
                <span>Total</span>
                <span>R$ {(100 + 100 * taxPercentage / 100).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Instrução */}
          <div className="tax-instruction">
            <h4>
              <AppIcon name="info" />
              Como Funciona
            </h4>
            <ul>
              <li>A taxa será calculada sobre o valor total do pedido</li>
              <li>O garçom será instruído a mencionar a taxa na resposta</li>
              <li>Exemplo: "Total: R$ 110,00 (incluso 10% de taxa de serviço)"</li>
              <li>A configuração é dinâmica e pode ser alterada a qualquer momento</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <AppIcon name="progress_activity" className="spinning" />
                Salvando...
              </>
            ) : (
              <>
                <AppIcon name="check" />
                Salvar Configuração
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
